import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { companyMapAtom } from '../../../services/hooks/useSaveCompanyData';
import {
  fetchLegalStructureGraph,
  ILegalStructureEntityRow,
  ILegalStructureGraphResponse,
} from '../../../services/queries/MaggieLegalStructureQueries';
import { fundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { dateToPlainDateString } from '../../../util/date-utilts';
import { useLegalStructureRegister } from '../../EntityManagement/hooks/useLegalStructureRegister';
import {
  useStructureChartFamilies,
  useStructureChartRootFunds,
} from '../../EntityManagement/surfaces/StructureChart/useStructureChartGraph';
import { allInvestmentRoundsMapStateJ } from '../../Finance2/state/FinanceStateJ';
import { useColumns } from '../hooks/useColumns/useColumns';
import { selectedViewDatePFJ } from '../state/PageStateJ';
import { buildPortfolioTree } from './buildPortfolioTree';
import { computeTreeRows, IPortfolioTreeRow } from './computeTreeRows';
import { PortfolioTreeMode } from './portfolioTree.types';

/** `fetchLegalStructureGraph` resolves null on 404/501 — an undeployed endpoint, not "no
 * structure for this fund". */
export function deriveTreeUnavailable(args: {
  enabled: boolean;
  isLoading: boolean;
  register: ILegalStructureEntityRow[] | null | undefined;
  rootFundCount: number;
  allGraphsNull: boolean;
}): boolean {
  return (
    args.enabled &&
    !args.isLoading &&
    (args.register === null || (args.rootFundCount > 0 && args.allGraphsNull))
  );
}

/** One graph query per root fund plus one per family (FD-4940): fund roots never carry
 * their MEMBERSHIP parent, so the family tier's edges only exist in family-rooted
 * graphs. The family keys match the structure chart's, sharing its cache entries. */
export function treeGraphQueries(
  rootFunds: { id: number }[],
  families: { id: number }[],
  asOf: string,
  enabled: boolean
) {
  return [
    ...rootFunds.map((fund) => ({
      queryKey: ['legalStructureGraph', fund.id, asOf] as const,
      queryFn: () => fetchLegalStructureGraph({ rootType: 'fund' as const, rootId: fund.id, asOf }),
      enabled,
      retry: false as const,
    })),
    ...families.map((family) => ({
      queryKey: ['legalStructureGraph', 'family', family.id, asOf] as const,
      queryFn: () => fetchLegalStructureGraph({ rootType: 'family' as const, rootId: family.id, asOf }),
      enabled,
      retry: false as const,
    })),
  ];
}

export function usePortfolioTree(
  metrics: IMetricsDataModel[] | undefined,
  mode: PortfolioTreeMode,
  enabled: boolean,
  /** Overrides the grid's own as-of date, for callers outside the Portfolio Overview page. */
  options?: { asOfDate?: Date }
): {
  rows: IPortfolioTreeRow[] | undefined;
  /** The build's mode-aware leaf listing per node, for re-pooling over a filtered leaf set. */
  leafKeysByNode: ReadonlyMap<string, readonly string[]> | undefined;
  isLoading: boolean;
  isUnavailable: boolean;
} {
  const rootFunds = useStructureChartRootFunds(enabled);
  const families = useStructureChartFamilies(enabled);
  const { data: register, isLoading: registerLoading } = useLegalStructureRegister(enabled);
  const fundsById = useAtomValue(fundsByIdMapAtom);
  const companyMap = useAtomValue(companyMapAtom);
  const investmentMap = useAtomValue(allInvestmentRoundsMapStateJ);
  const columnsMeta = useColumns();
  const viewDate = useAtomValue(selectedViewDatePFJ);
  const date = options?.asOfDate ?? viewDate;
  const asOf = dateToPlainDateString(date ?? new Date());

  const combine = useCallback(
    (results: UseQueryResult<ILegalStructureGraphResponse | null>[]) => ({
      graphs: results.map((r) => r.data).filter((g): g is ILegalStructureGraphResponse => g != null),
      isLoading: results.some((r) => r.isLoading),
      allGraphsNull: results.length > 0 && results.every((r) => !r.isLoading && r.data === null),
    }),
    []
  );

  const {
    graphs,
    isLoading: graphsLoading,
    allGraphsNull,
  } = useQueries({
    queries: treeGraphQueries(rootFunds, families, asOf, enabled),
    // Structural sharing keeps `graphs` referentially stable only while `combine` itself is
    // stable, hence the useCallback; otherwise the `rows` useMemo below re-runs every render.
    combine,
  });

  const isLoading = enabled && (registerLoading || graphsLoading);
  const isUnavailable = deriveTreeUnavailable({
    enabled,
    isLoading,
    register,
    rootFundCount: rootFunds.length,
    allGraphsNull,
  });

  const built = useMemo(() => {
    if (!enabled || !metrics || isLoading || graphs.length === 0) {
      return undefined;
    }
    return buildPortfolioTree({ graphs, metrics, fundsById, mode });
  }, [enabled, metrics, isLoading, graphs, fundsById, mode]);

  const rows = useMemo(() => {
    if (!built || !metrics) {
      return undefined;
    }
    const entityValues = new Map(
      (register ?? []).map((row) => [
        `${row.ref.nodeType}-${row.ref.nodeId}`,
        {
          cost: row.cost ?? null,
          fmv: row.fmv ?? null,
          source: row.ledgerBacked ? 'ledger' : 'estimated',
        },
      ])
    );
    return computeTreeRows(built, {
      fundsById,
      companyMap,
      investmentMap,
      customFundFields: columnsMeta.filter((col) => col.path.startsWith('fundCompany')),
      asOfDate: date ?? new Date(),
      entityValues,
    });
  }, [built, metrics, register, fundsById, companyMap, investmentMap, columnsMeta, date]);

  return { rows, leafKeysByNode: built?.leafKeysByNode, isLoading, isUnavailable };
}
